import {
  Panel,
  PanelType,
  Stack,
  TextField,
  StackItem,
  DefaultButton,
  Checkbox,
  IComboBoxOption,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Label,
} from '@fluentui/react';
import { Editor } from '@tinymce/tinymce-react';
import { IMailTemplate } from '../../utils/MailTemplate';
import { customPanelStyles } from '../../theme';
import { modalContentStyles } from '../../utils';
import SaveButtonWithPermissions from '../../components/parts/SaveButtonWithPermissions';
import { ComboboxWithFilter } from '../../components/parts';
import { toastSuccess } from '../../utils/toast';

interface Props {
  isOpen: any;
  dismissPanel: any;
  saveTemplate: any;
  template: IMailTemplate | undefined;
  setTemplate: any;
  refetch: any;
}

const MailDetail = ({
  isOpen,
  dismissPanel,
  saveTemplate,
  template,
  setTemplate,
  refetch,
}: Props) => {
  const onChangeTextFieldValue = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string,
  ) => {
    setTemplate((prevState: any) => ({
      ...prevState,
      [(event.target as HTMLTextAreaElement).name]: newValue || '',
    }));
  };

  return (
    <Panel
      isLightDismiss
      isOpen={isOpen}
      onDismiss={dismissPanel}
      closeButtonAriaLabel='Close'
      headerText={
        template && template.id
          ? 'Mail sjabloon wijzigen'
          : 'Mail sjabloon toevoegen'
      }
      type={PanelType.custom}
      customWidth='1000px'
      styles={customPanelStyles}
    >
      <div>
        <div className={modalContentStyles.header} />
        <div className={modalContentStyles.body}>
          <p>ID: {template?.id}</p>
          <Stack>
            <Stack style={{ marginTop: 10, marginBottom: 10 }}>
              <Checkbox
                name='active'
                label='Actief?'
                checked={template && template.active}
                onChange={() => {
                  setTemplate((prevState: any) => ({
                    ...prevState,
                    active:
                      template && template.active ? !template.active : true,
                  }));
                }}
              />
            </Stack>
            <Stack style={{ marginBottom: 10 }}>
              <ComboboxWithFilter
                label='Type'
                options={[
                  {
                    key: 'Uitgaande factuur',
                    text: 'Uitgaande factuur',
                  },
                  { key: 'Bestelbon', text: 'Bestelbon' },
                  { key: 'Meerwerk', text: 'Meerwerk' },
                  { key: 'Billit factuur', text: 'Billit factuur' },
                ]}
                value={template && template.type ? template.type : ''}
                multiline={false}
                callBack={(newValue: IComboBoxOption[]) => {
                  if (newValue && newValue.length > 0) {
                    setTemplate((prevState: any) => ({
                      ...prevState,
                      type: newValue[0].key,
                    }));
                  }
                }}
                required
                errorMessage={
                  template && !template.type ? 'Dit veld is verplicht' : ''
                }
              />
            </Stack>
            <Stack style={{ marginBottom: 10 }}>
              <TextField
                name='name'
                label='Naam'
                value={template && template.name ? template.name : ''}
                onChange={onChangeTextFieldValue}
                required
                errorMessage={
                  template && !template.name ? 'Dit veld is verplicht' : ''
                }
              />
            </Stack>
            <Stack style={{ marginBottom: 10 }}>
              <TextField
                name='comments'
                label='Beschrijving'
                value={template && template.comments ? template.comments : ''}
                onChange={onChangeTextFieldValue}
                multiline
                rows={2}
              />
            </Stack>
            {template?.type === 'Uitgaande factuur' && (
              <>
                <Label>Variabelen</Label>
                <div
                  style={{ border: '1px solid #565655', borderRadius: '2px' }}
                >
                  <DetailsList
                    items={[
                      {
                        key: '0',
                        naam: 'Factuur nummer',
                        waarde: 'invoice_no',
                        voorbeeld: '402.24',
                      },
                      {
                        key: '1',
                        naam: 'Factuur datum',
                        waarde: 'invoice_date',
                        voorbeeld: '20/06/2024',
                      },

                      {
                        key: '2',
                        naam: 'Werf naam',
                        waarde: 'construction_site_name',
                        voorbeeld: 'Poortmans - Van de Vondel',
                      },
                      {
                        key: '3',
                        naam: 'Werf address',
                        waarde: 'construction_site_address',
                        voorbeeld: 'Cuperuslei 2 bus 1',
                      },
                      {
                        key: '4',
                        naam: 'Werf postcode',
                        waarde: 'construction_site_zip_code',
                        voorbeeld: '2220',
                      },
                      {
                        key: '5',
                        naam: 'Werf plaats',
                        waarde: 'construction_site_city',
                        voorbeeld: 'Heist-op-den-Berg',
                      },
                      {
                        key: '6',
                        naam: 'Klant naam',
                        waarde: 'customer_name',
                        voorbeeld: 'Hans en Ellen',
                      },
                      {
                        key: '7',
                        naam: 'Betalingsschijf',
                        waarde: 'payment_slip',
                        voorbeeld: '5de betalingsschijf',
                      },
                      {
                        key: '8',
                        naam: 'Werfleider',
                        waarde: 'site_manager',
                        voorbeeld: 'Eran Wens',
                      },
                      {
                        key: '9',
                        naam: 'Verkoper',
                        waarde: 'sales_rep',
                        voorbeeld: 'Marc Vermoten',
                      },
                    ]}
                    columns={[
                      {
                        key: 'column1',
                        name: 'Naam',
                        fieldName: 'naam',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                      },
                      {
                        key: 'column2',
                        name: 'Waarde',
                        fieldName: 'waarde',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                        onRender: item => (
                          <span
                            style={{
                              color: '#71BF44',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                            role='button'
                            tabIndex={0}
                            onClick={() => {
                              navigator.clipboard.writeText(`[${item.waarde}]`);
                              toastSuccess(
                                `Copied "${item.waarde}" to clipboard!`,
                              );
                            }}
                            onKeyPress={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                navigator.clipboard.writeText(
                                  `[${item.waarde}]`,
                                );
                                toastSuccess(
                                  `Copied "${item.waarde}" to clipboard!`,
                                );
                              }
                            }}
                          >
                            {item.waarde}
                          </span>
                        ),
                      },
                      {
                        key: 'column3',
                        name: 'Voorbeeld',
                        fieldName: 'voorbeeld',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                      },
                    ]}
                    setKey='set'
                    layoutMode={DetailsListLayoutMode.justified}
                    selectionMode={SelectionMode.none} // Disable selection dots
                  />
                </div>
                <br />
              </>
            )}

            {template?.type === 'Meerwerk' && (
            <>
              <Label>Variabelen</Label>
              <div
                style={{ border: '1px solid #565655', borderRadius: '2px' }}
              >
                <DetailsList
                  items={[
                      {
                        key: '0',
                        naam: 'Document nummer',
                        waarde: 'document_no',
                        voorbeeld: '402.24',
                      },
                      {
                        key: '1',
                        naam: 'Document datum',
                        waarde: 'document_date',
                        voorbeeld: '20/06/2024',
                      },

                      {
                        key: '2',
                        naam: 'Werf naam',
                        waarde: 'construction_site_name',
                        voorbeeld: 'Poortmans - Van de Vondel',
                      },
                      {
                        key: '3',
                        naam: 'Werf address',
                        waarde: 'construction_site_address',
                        voorbeeld: 'Cuperuslei 2 bus 1',
                      },
                      {
                        key: '4',
                        naam: 'Werf postcode',
                        waarde: 'construction_site_zip_code',
                        voorbeeld: '2220',
                      },
                      {
                        key: '5',
                        naam: 'Werf plaats',
                        waarde: 'construction_site_city',
                        voorbeeld: 'Heist-op-den-Berg',
                      },
                      {
                        key: '6',
                        naam: 'Klant naam',
                        waarde: 'customer_name',
                        voorbeeld: 'Hans en Ellen',
                      },

                      {
                        key: '8',
                        naam: 'Werfleider',
                        waarde: 'site_manager',
                        voorbeeld: 'Eran Wens',
                      },
                      {
                        key: '9',
                        naam: 'Verkoper',
                        waarde: 'sales_rep',
                        voorbeeld: 'Marc Vermoten',
                      },
                    ]}
                  columns={[
                      {
                        key: 'column1',
                        name: 'Naam',
                        fieldName: 'naam',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                      },
                      {
                        key: 'column2',
                        name: 'Waarde',
                        fieldName: 'waarde',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                        onRender: item => (
                          <span
                            style={{
                              color: '#71BF44',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                            role='button'
                            tabIndex={0}
                            onClick={() => {
                              navigator.clipboard.writeText(`[${item.waarde}]`);
                              toastSuccess(
                                `Copied "${item.waarde}" to clipboard!`,
                              );
                            }}
                            onKeyPress={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                navigator.clipboard.writeText(
                                  `[${item.waarde}]`,
                                );
                                toastSuccess(
                                  `Copied "${item.waarde}" to clipboard!`,
                                );
                              }
                            }}
                          >
                            {item.waarde}
                          </span>
                        ),
                      },
                      {
                        key: 'column3',
                        name: 'Voorbeeld',
                        fieldName: 'voorbeeld',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                      },
                    ]}
                  setKey='set'
                  layoutMode={DetailsListLayoutMode.justified}
                  selectionMode={SelectionMode.none}
                />
              </div>
              <br />
            </>
            )}

            {template?.type === 'Bestelbon' && (
              <>
                <Label>Variabelen</Label>
                <div
                  style={{ border: '1px solid #565655', borderRadius: '2px' }}
                >
                  <DetailsList
                    items={[
                      {
                        key: '0',
                        naam: 'Bestelbon nummer',
                        waarde: 'purchase_order_no',
                        voorbeeld: '529.24',
                      },
                      {
                        key: '1',
                        naam: 'Omschrijving (intern)',
                        waarde: 'internal_description',
                        voorbeeld: 'Aanvullen fundering ',
                      },
                      {
                        key: '2',
                        naam: 'Werf naam',
                        waarde: 'construction_site_name',
                        voorbeeld: 'Poortmans - Van de Vondel',
                      },
                      {
                        key: '3',
                        naam: 'Leveringsadres',
                        waarde: 'delivery_site_address',
                        voorbeeld: 'Cuperuslei 2 bus 1',
                      },
                      {
                        key: '4',
                        naam: 'Leveringsadres postcode',
                        waarde: 'delivery_site_zip_code',
                        voorbeeld: '2220',
                      },
                      {
                        key: '5',
                        naam: 'Leveringsadres plaats',
                        waarde: 'delivery_site_city',
                        voorbeeld: 'Heist-op-den-Berg',
                      },
                      {
                        key: '6',
                        naam: 'Klant naam',
                        waarde: 'customer_name',
                        voorbeeld: 'Hans en Ellen',
                      },
                      {
                        key: '7',
                        naam: 'Contactpersoon',
                        waarde: 'contact_person',
                        voorbeeld: 'Eric',
                      },
                    ]}
                    columns={[
                      {
                        key: 'column1',
                        name: 'Naam',
                        fieldName: 'naam',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                      },
                      {
                        key: 'column2',
                        name: 'Waarde',
                        fieldName: 'waarde',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                        onRender: item => (
                          <span
                            style={{
                              color: '#71BF44',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                            role='button'
                            tabIndex={0}
                            onClick={() => {
                              navigator.clipboard.writeText(`[${item.waarde}]`);
                              toastSuccess(
                                `Copied "${item.waarde}" to clipboard!`,
                              );
                            }}
                            onKeyPress={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                navigator.clipboard.writeText(
                                  `[${item.waarde}]`,
                                );
                                toastSuccess(
                                  `Copied "${item.waarde}" to clipboard!`,
                                );
                              }
                            }}
                          >
                            {item.waarde}
                          </span>
                        ),
                      },
                      {
                        key: 'column3',
                        name: 'Voorbeeld',
                        fieldName: 'voorbeeld',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                      },
                    ]}
                    setKey='set'
                    layoutMode={DetailsListLayoutMode.justified}
                    selectionMode={SelectionMode.none} // Disable selection dots
                  />
                </div>
                <br />
              </>
            )}

            {template?.type === 'Billit factuur' && (
              <>
                <Label>Variabelen</Label>
                <div
                  style={{ border: '1px solid #565655', borderRadius: '2px' }}
                >
                  <DetailsList
                    items={[
                      {
                        key: '0',
                        naam: 'Factuurnummer',
                        waarde: 'invoice_number',
                        voorbeeld: 'F-2026-VER-2631',
                      },
                      {
                        key: '1',
                        naam: 'Leverancier',
                        waarde: 'supplier_name',
                        voorbeeld: 'CENTERBETON NV',
                      },
                      {
                        key: '2',
                        naam: 'Factuurdatum',
                        waarde: 'invoice_date',
                        voorbeeld: '30/06/2026',
                      },
                      {
                        key: '3',
                        naam: 'Vervaldatum',
                        waarde: 'expiry_date',
                        voorbeeld: '30/07/2026',
                      },
                      {
                        key: '4',
                        naam: 'Totaal incl. BTW',
                        waarde: 'total_incl',
                        voorbeeld: '1799.46',
                      },
                      {
                        key: '5',
                        naam: 'Totaal excl. BTW',
                        waarde: 'total_excl',
                        voorbeeld: '1487.16',
                      },
                      {
                        key: '6',
                        naam: 'BTW bedrag',
                        waarde: 'total_vat',
                        voorbeeld: '312.30',
                      },
                      {
                        key: '7',
                        naam: 'Munteenheid',
                        waarde: 'currency',
                        voorbeeld: 'EUR',
                      },
                      {
                        key: '8',
                        naam: 'Referentie',
                        waarde: 'reference',
                        voorbeeld: '25309222',
                      },
                      {
                        key: '9',
                        naam: 'Betalingsreferentie',
                        waarde: 'payment_reference',
                        voorbeeld: '+++123/4567/89012+++',
                      },
                      {
                        key: '10',
                        naam: 'BTW-nummer leverancier',
                        waarde: 'supplier_vat',
                        voorbeeld: 'BE0123456789',
                      },
                    ]}
                    columns={[
                      {
                        key: 'column1',
                        name: 'Naam',
                        fieldName: 'naam',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                      },
                      {
                        key: 'column2',
                        name: 'Waarde',
                        fieldName: 'waarde',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                        onRender: item => (
                          <span
                            style={{
                              color: '#71BF44',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                            role='button'
                            tabIndex={0}
                            onClick={() => {
                              navigator.clipboard.writeText(`[${item.waarde}]`);
                              toastSuccess(
                                `Copied "${item.waarde}" to clipboard!`,
                              );
                            }}
                            onKeyPress={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                navigator.clipboard.writeText(
                                  `[${item.waarde}]`,
                                );
                                toastSuccess(
                                  `Copied "${item.waarde}" to clipboard!`,
                                );
                              }
                            }}
                          >
                            {item.waarde}
                          </span>
                        ),
                      },
                      {
                        key: 'column3',
                        name: 'Voorbeeld',
                        fieldName: 'voorbeeld',
                        minWidth: 100,
                        maxWidth: 200,
                        isResizable: true,
                      },
                    ]}
                    setKey='set'
                    layoutMode={DetailsListLayoutMode.justified}
                    selectionMode={SelectionMode.none}
                  />
                </div>
                <br />
              </>
            )}
            <Stack>
              <Label>Body</Label>
              <Editor
                apiKey='wtesrjtl8nmcrvthwl673ws74pw5p7xkf7x6co0o5rt8lhes'
                init={{
                  height: 400,
                  menubar: false,

                  menu: {
                    file: { title: 'File', items: 'newdocument' },
                    edit: {
                      title: 'Edit',
                      items: 'undo redo | cut copy paste pastetext | selectall',
                    },
                    insert: { title: 'Insert', items: 'link emplate' },
                    format: {
                      title: 'Format',
                      items: 'bold italic underline',
                    },
                  },
                  plugins: [
                    'advlist',
                    'autolink',
                    'lists',
                    'link',
                    'image',
                    'charmap',
                    'preview',
                    'anchor',
                    'searchreplace',
                    'visualblocks',
                    'code',
                    'fullscreen',
                    'insertdatetime',
                    'media',
                    'table',
                    'help',
                    'wordcount',
                  ],
                  toolbar:
                    'undo redo | blocks | ' +
                    'bold italic | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat',

                  setup(editor) {
                    editor.on('init', () => {
                      // Override TinyMCE default styles
                      const customStyles = `
                        /* Main container border */
                        .tox.tox-tinymce {
                          border: 1px solid #605E5C !important;
                          border-radius: 2px !important;
                        }
                        .tox.tox-tinymce:focus-within,
                        .tox.tox-tinymce--focused {
                          outline: none !important;
  
                          border-radius: 2px !important;

                        }
                        
                        /* Menu items and buttons */
                        .tox .tox-mbtn--active,
                        .tox .tox-tbtn--enabled,
                        .tox .tox-tbtn:hover,
                        .tox .tox-split-button:hover,
                        .tox .tox-collection__item--active,
                        .tox .tox-collection__item:hover {
                          background-color: rgba(76, 175, 80, 0.1) !important;
                          color: inherit !important;
                        }

                        /* Menu button focus state */
                        .tox .tox-mbtn:focus:not(:disabled)::after {
                          border-radius: 3px;
                          bottom: 0;
                          box-shadow: 0 0 0 2px #71bf43;
                          content: '';
                          left: 0;
                          position: absolute;
                          right: 0;
                          top: 0;
                        }

                        /* Dropdown menus */
                  
                        
                        .tox .tox-collection--list .tox-collection__item--active {
                          background-color: rgba(76, 175, 80, 0.1) !important;
                        }

                        /* Edit area specific styles */
                        .tox .tox-edit-area {
                          border: 1px solid transparent !important;
                        }
                        
                        .tox .tox-edit-area::before {
                          border: 2px solid #71bf43;
                          border-radius: 4px;
                          content: '';
                          inset: 0;
                          opacity: 0;
                          pointer-events: none;
                          position: absolute;
                          transition: opacity .15s;
                          z-index: 1;
                        }

                        .tox .tox-tbtn:focus::after {
                          border-radius: 3px;
                          bottom: 0;
                          box-shadow: 0 0 0 2px #71bf43;
                          content: '';
                          left: 0;
                          position: absolute;
                          right: 0;
                          top: 0;
                        }

                        .tox .tox-edit-area__iframe {
                          border: none !important;
                        }
                      `;

                      // Add styles to document head
                      const styleElement = document.createElement('style');
                      styleElement.textContent = customStyles;
                      document.head.appendChild(styleElement);

                      // Also add to editor to ensure styles are applied
                      editor.dom.addStyle(customStyles);
                    });
                  },
                }}
                value={template && template.body ? template.body : ''}
                onEditorChange={(e: any) => {
                  setTemplate((prevState: any) => ({
                    ...prevState,
                    body: e,
                  }));
                }}
              />
            </Stack>
          </Stack>
        </div>
        <div className={modalContentStyles.footer}>
          <Stack style={{ flexDirection: 'row', marginTop: 10 }}>
            <SaveButtonWithPermissions
              disabled={
                !template ||
                (template && !template.name) ||
                (template && !template.body) ||
                (template && !template.type)
              }
              save={saveTemplate}
              permission='write:mailTemplates'
            />

            <StackItem>
              <DefaultButton onClick={dismissPanel}>Annuleren</DefaultButton>
            </StackItem>
          </Stack>
        </div>
      </div>
    </Panel>
  );
};

export default MailDetail;
